import { useCallback, useContext, useMemo, useState } from "react";
import { AssessmentCategoryCell } from "pages/School/Assessments";
import { CircularProgress, IconButton, SvgIcon } from "@mui/material";
import { ReactComponent as EditIcon } from 'assets/icons/Edit.svg';
import { ReactComponent as VisibilityIcon } from 'assets/icons/Visibility.svg';
import { useNavigate } from "react-router-dom";
import { app } from "router/routes";
import { assesmentDefStatuses, categories } from "keys";
import { AssessmentLibraryContext } from "../AssessmentLibraryContext";
import { TransparentIcon } from "styles/GlobalStyle";
import { ReactComponent as ArchivesIcon } from 'assets/icons/Archives.svg';
import { ReactComponent as CopyIcon } from 'assets/icons/CopyItem.svg';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createDuplicateAssessmentDefFetch, updateAssessmentDefFetch, updateAssessmentDefStatusFetch } from "store/middlewares/assessment.crud";
import { queryClient as qc } from 'keys'
import StatusCell from "../StatusCell";

const useColumns = () => {
    const { setCurrentAssessmentDef, setOpenAssesmentDefDrawer } = useContext(AssessmentLibraryContext)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [currentAssessment, setCurrentAssessment] = useState(null)

    const { mutateAsync: updateArchive, isPending: updateArchivePending } = useMutation({
        mutationFn: async (data) => {
            await updateAssessmentDefStatusFetch({ ...data, status: assesmentDefStatuses.ARCHIVED })
        },
        onSuccess: () => {
            queryClient.invalidateQueries([qc.assessmentLibrary])
        }
    })

    const { mutateAsync: createDuplicate, isPending: createDuplicatePending } = useMutation({
        mutationFn: async (id) => {
            await createDuplicateAssessmentDefFetch(id)
        },
        onSuccess: () => {
            queryClient.invalidateQueries([qc.assessmentLibrary])
        }
    })

    const goEditAssessmentPage = useCallback((row) => {
        navigate(`${app.assessmentLibrary}/${row.original.id}`)
    }, [])

    const moveToArchive = useCallback((data) => {
        setCurrentAssessment(data.id)
        updateArchive(data)
    }, [])

    const createDuplicateAssessment = useCallback((id) => {
        setCurrentAssessment(id)
        createDuplicate(id)
    }, [])

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: "ID",
            },
            {
                accessorKey: 'category_name',
                header: "סוג מבדק",
                cell: AssessmentCategoryCell,
                meta: {
                    name: "category_name",
                    label: "category_label"
                }
            },
            {
                accessorKey: 'name',
                header: "שם טכני",
            },
            {
                accessorKey: 'title',
                header: "כותרת לתצוגה",
            },
            {
                accessorKey: 'readability_score_name',
                header: "שנתון וסמסטר",
            },
            {
                accessorKey: 'author',
                header: "יוצר.ת",
                cell: ({ getValue, row }) => getValue() ? `${getValue()} ${row.original.publisher ? `, ${row.original.publisher}` : ""}` : "",
                enableSorting: false
            },
            {
                accessorKey: 'affiliation_name',
                header: "בשימוש מוסד",
            },
            {
                accessorKey: 'performance_quantity',
                header: "כמות ביצועים",
                enableSorting: false
            },
            {
                accessorKey: 'status',
                header: "סטטוס",
                cell: StatusCell
            },
            {
                accessorKey: 'reports',
                header: "צפיה",
                enableSorting: false,
                cell: ({ row }) => {
                    return [
                        <TransparentIcon>
                            <IconButton
                                className="textPrimary"
                                onClick={() => {
                                    setCurrentAssessmentDef({ id: row.original.id, categoryName: row.original.category_name })
                                    setOpenAssesmentDefDrawer(true)
                                }}
                            >
                                <SvgIcon component={VisibilityIcon} inheritViewBox />
                            </IconButton>
                        </TransparentIcon>
                    ];
                }
            },
            {
                accessorKey: 'edit',
                header: "עריכה",
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <IconButton
                            className="textPrimary"
                            onClick={() => goEditAssessmentPage(row)}
                        >
                            <SvgIcon component={EditIcon} inheritViewBox />
                        </IconButton>
                    );
                }
            },
            {
                accessorKey: 'archive',
                header: "ארכיון",
                enableSorting: false,
                cell: ({ row }) => {
                    const loading = updateArchivePending && row.original.id == currentAssessment
                    return (
                        <IconButton
                            disabled={loading}
                            className="textPrimary"
                            onClick={() => moveToArchive(row.original)}
                        >
                            {loading ?
                                <CircularProgress size={20} color="inherit" /> :
                                <SvgIcon fontSize="small" component={ArchivesIcon} inheritViewBox />}
                        </IconButton>
                    );
                }
            },
            {
                accessorKey: 'duplicate',
                header: "שיכפול",
                enableSorting: false,
                cell: ({ row }) => {
                    const loading = createDuplicatePending && row.original.id == currentAssessment
                    return (
                        <IconButton
                            disabled={loading}
                            className="textPrimary"
                            onClick={() => createDuplicateAssessment(row.original.id)}
                        >
                            {loading ?
                                <CircularProgress size={20} color="inherit" /> :
                                <SvgIcon fontSize="small" component={CopyIcon} inheritViewBox />}
                        </IconButton>
                    );
                }
            }
        ], [createDuplicatePending, updateArchivePending, currentAssessment]
    )

    return columns
}

export default useColumns